/**
 * Seuils du feuillet `Values` du PAD (0/7/14) — le hub les applique, il ne les
 * redéfinit pas. Fonction pure : c'est la SEULE source de vérité du retard, pour
 * que pastille, KPI, rappel et vue transverse ne puissent pas se contredire.
 */
import type { FvStatus, Milestone } from '@altyor-kronos/shared-types';

/**
 * Une fiche compte comme « faite » sur ces statuts seulement. `refused`,
 * `not-pass`, `sent` et `not-done` sont des états ouverts — les compter comme
 * faits gonflerait les KPI et cacherait exactement ce qu'ils doivent montrer.
 */
export const FV_DONE_STATUSES: readonly FvStatus[] = ['validated', 'pass', 'derogated', 'na'];

export type ComplianceState = 'done' | 'upcoming' | 'ok' | 'acceptable' | 'nok';

export interface Compliance {
  state: ComplianceState;
  /** Jours calendaires de retard, 0 si à venir ou franchi. */
  daysLate: number;
  /** Date qui fait foi : replanifiée si présente, sinon cible. */
  effectiveDate: Date | null;
}

const DAY_MS = 86_400_000;

/**
 * Normalisation en jours calendaires **UTC**, pas locaux : toute l'app écrit ses
 * dates de jalon en minuit-UTC (`new Date('2026-08-07')` à la saisie,
 * `.toISOString().split('T')[0]` à la relecture). Des getters locaux feraient
 * dépendre le retard du fuseau du navigateur — inacceptable pour FR + CN.
 */
const startOfDay = (d: Date) => Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());

export function milestoneCompliance(milestone: Milestone, now: Date): Compliance {
  const due = milestone.rescheduledDate ?? milestone.targetDate;
  const effectiveDate = due ? due.toDate() : null;
  if (milestone.status === 'done') return { state: 'done', daysLate: 0, effectiveDate };
  if (!effectiveDate) return { state: 'upcoming', daysLate: 0, effectiveDate: null };

  const daysLate = Math.floor((startOfDay(now) - startOfDay(effectiveDate)) / DAY_MS);
  // Échu du jour même = 0 j de retard, donc `ok` au sens du feuillet Values
  // (« retard ≤ 7 j »). Seul un futur strict est `upcoming`.
  if (daysLate < 0) return { state: 'upcoming', daysLate: 0, effectiveDate };
  if (daysLate <= 7) return { state: 'ok', daysLate, effectiveDate };
  if (daysLate <= 14) return { state: 'acceptable', daysLate, effectiveDate };
  return { state: 'nok', daysLate, effectiveDate };
}

// ─── Vue transverse (écran 4) ───────────────────────────────────

/** Du plus grave au plus anodin — sert à agréger un projet sur son pire jalon. */
const SEVERITY: Record<ComplianceState, number> = {
  nok: 3, acceptable: 2, ok: 1, upcoming: 0, done: 0,
};

/** Ce que la vue lit d'un projet — structurel, pour accepter `Project` tel quel. */
export interface ProjectLifeInput {
  code?: string;
  name?: string;
  clientId?: string;
  currentProgram?: string;
  team?: { managers?: string[] };
}

export interface ProjectLifeRow {
  projectId: string;
  code: string;
  name: string;
  clientId: string;
  managerUids: string[];
  program: string;
  /** Premier jalon non franchi portant une revue formelle. */
  nextReview: { milestoneId: string; name: string } | null;
  /** Échéance du jalon qui décide de l'état — celui qui est le plus en retard. */
  dueDate: Date | null;
  state: ComplianceState;
  daysLate: number;
  fvDone: number;
  fvTotal: number;
}

/**
 * Croise projets × jalons pour la page « Vie des projets ». Pur et testable :
 * la page se contente de l'appeler dans un `useMemo`, donc les tests portent
 * sur la même fonction que l'écran.
 *
 * L'état d'un projet est celui de son **pire** jalon non franchi — c'est ce que
 * la direction veut voir en une colonne, pas une moyenne qui noierait un NOK.
 */
export function buildProjectLifeRows(
  projects: ReadonlyArray<{ id: string; data: ProjectLifeInput }>,
  milestones: ReadonlyArray<Milestone & { id: string; projectId: string }>,
  now: Date,
): ProjectLifeRow[] {
  const byProject = new Map<string, Array<Milestone & { id: string }>>();
  for (const m of milestones) {
    const list = byProject.get(m.projectId);
    if (list) list.push(m);
    else byProject.set(m.projectId, [m]);
  }

  return projects.map(({ id, data: p }) => {
    const list = (byProject.get(id) ?? []).slice().sort((a, b) => a.order - b.order);

    let state: ComplianceState = 'upcoming';
    let daysLate = 0;
    let dueDate: Date | null = null;
    let fvDone = 0;
    let fvTotal = 0;
    let nextReview: ProjectLifeRow['nextReview'] = null;

    for (const m of list) {
      const c = milestoneCompliance(m, now);
      if (c.state !== 'done' && SEVERITY[c.state] > SEVERITY[state]) {
        state = c.state;
        daysLate = c.daysLate;
        dueDate = c.effectiveDate;
      }
      if (!nextReview && m.reviewName && m.status !== 'done') {
        nextReview = { milestoneId: m.id, name: m.reviewName };
        // Sans jalon en retard, l'échéance affichée est celle de la revue à venir.
        if (!dueDate) dueDate = c.effectiveDate;
      }
      for (const d of m.deliverables ?? []) {
        fvTotal += 1;
        if (FV_DONE_STATUSES.includes(d.status)) fvDone += 1;
      }
    }

    return {
      projectId: id,
      code: p.code ?? id,
      name: p.name ?? '',
      clientId: p.clientId ?? '',
      managerUids: p.team?.managers ?? [],
      program: p.currentProgram ?? '',
      nextReview,
      dueDate,
      state,
      daysLate,
      fvDone,
      fvTotal,
    };
  });
}

/** Couleur MUI associée — un seul mapping pour toute l'app. */
export const COMPLIANCE_COLOR: Record<ComplianceState, 'default' | 'success' | 'warning' | 'error'> = {
  done: 'success', upcoming: 'default', ok: 'success', acceptable: 'warning', nok: 'error',
};
